import type { IconsType } from '@arvoreeducacao/bonsai-core'
import { tokens } from '@arvoreeducacao/bonsai-ds'
import { useHistory } from 'react-router-dom'
import {
  ACTIVITIES_PATH,
  ATUALIDADES_HOME_ROOT_PATH,
  COMUNICAR_HOME_ROOT_PATH,
  EXPLORE_PATH,
  FOREST_PATH,
  INGLES_HOME_ROOT_PATH,
  LEAGUE_PATH,
  LER_HOME_ROOT_PATH,
  LER_REVISTAS_E_JORNAIS_HOME_ROOT_PATH,
  LER_NOTICIAS_HOME_ROOT_PATH,
  LIVROS_HOME_ROOT_PATH,
  MY_READINGS_MODULAR_PATH,
  LER_NEWSSTAND_ROOT_PATH,
  ROOT_PATH,
  SENTIR_HOME_ROOT_PATH,
  SENTIR_DIARIO_HOME_ROOT_PATH,
  USER_PROFILE_MENU
} from 'routes/paths'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import useA11yEnabled from 'utils/hooks/use-a11y-enabled'
import { useTranslation } from 'i18n'

const BOTTOM_NAV_LABELS = {
  home: 'Início',
  search: 'Buscar',
  medal: 'Conquistas',
  'book-person': 'Minhas leituras',
  book: 'Minhas leituras',
  'clipboard-content': 'Atividades',
  menu: 'Menu',
  'newspaper-alt': 'Revistas e jornais',
  'flower-outline': 'Flora AI'
} as const

export const useFooter = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const { user } = useUserInfo()
  const { a11yEnabled } = useA11yEnabled()
  const gamificationPath = user.canAccessForest ? FOREST_PATH : LEAGUE_PATH
  const activeItemColor = a11yEnabled ? tokens.colors.primary900 : undefined

  const getMenuItem = (
    iconName: IconsType,
    iconNameFilled: IconsType,
    route: string,
    canAccess: boolean
  ) => {
    const currentPath = history?.location.pathname
    const modularHomeSubDomains = [
      LIVROS_HOME_ROOT_PATH,
      ATUALIDADES_HOME_ROOT_PATH,
      INGLES_HOME_ROOT_PATH,
      SENTIR_HOME_ROOT_PATH,
      SENTIR_DIARIO_HOME_ROOT_PATH,
      LER_HOME_ROOT_PATH,
      LER_REVISTAS_E_JORNAIS_HOME_ROOT_PATH,
      LER_NOTICIAS_HOME_ROOT_PATH,
      COMUNICAR_HOME_ROOT_PATH
    ]
    const isHomePageActive =
      route === ROOT_PATH &&
      modularHomeSubDomains.some(subDomain => currentPath === subDomain)
    const isActive = currentPath === route || isHomePageActive

    return canAccess
      ? {
          active: isActive,
          activeColor: activeItemColor,
          ariaLabel: t(
            BOTTOM_NAV_LABELS[iconName as keyof typeof BOTTOM_NAV_LABELS] ??
              'Menu'
          ),
          iconName: isActive ? iconNameFilled : iconName,
          onClick: () => {
            if (route.includes('http') || route.startsWith('/app-v2')) {
              window.location.href = route
              return
            }

            history.push(route)
          }
        }
      : null
  }

  const items = [
    getMenuItem('home', 'home-filled', ROOT_PATH, true),
    getMenuItem('search', 'search-filled', EXPLORE_PATH, true),
    getMenuItem(
      'medal',
      'medal-filled',
      gamificationPath,
      user.canAccessLeagueContent || user.canAccessForest
    ),
    getMenuItem(
      'book-person',
      'book-person-filled',
      '/app-v2/my-readings',
      user.canAccessMyReadings
    ),
    getMenuItem(
      'clipboard-content',
      'clipboard-content-filled',
      user?.featureToggles?.activitiesV3
        ? '/app-v2/activities'
        : ACTIVITIES_PATH,
      user.canAccessActivities
    )
  ].filter(item => item !== null)

  const itemsForSomos = [
    getMenuItem('home', 'home-filled', LER_NEWSSTAND_ROOT_PATH, true),
    getMenuItem('search', 'search-filled', EXPLORE_PATH, true),
    getMenuItem(
      'clipboard-content',
      'clipboard-content-filled',
      user?.featureToggles?.activitiesV3
        ? '/app-v2/activities'
        : ACTIVITIES_PATH,
      true
    ),
    getMenuItem('menu', 'menu-filled', USER_PROFILE_MENU, true)
  ]

  const itemsForFreemium = [
    getMenuItem('home', 'home-filled', ROOT_PATH, true),
    getMenuItem('search', 'search-filled', EXPLORE_PATH, true),
    getMenuItem('book', 'book', MY_READINGS_MODULAR_PATH, true),
    getMenuItem('menu', 'menu-filled', USER_PROFILE_MENU, true)
  ]

  const itemsForRedBalloon = [
    ...items,
    getMenuItem(
      'newspaper-alt',
      'newspaper-alt-filled',
      LER_NEWSSTAND_ROOT_PATH,
      user.canAccessPressreaderContent
    )
  ]

  const itemsForOttoAi = [
    ...items,
    user.teacher?.id !== null &&
      getMenuItem(
        'flower-outline',
        'flower-filled',
        '/app-v2/arvore-intelligence',
        true
      )
  ]

  const renderingItems = () => {
    if (user.isClientSomos) return itemsForSomos
    if (user.isFreemium) return itemsForFreemium
    if (user.featureToggles?.showPressreaderIconOnHeader) {
      return itemsForRedBalloon
    }
    if (user.featureToggles?.ottoAiEnabled) {
      return itemsForOttoAi
    }

    return items
  }

  return { renderingItems }
}
